import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { ArrowLeftRight } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getTransfer } from '../../api/transfers/get-transfer'
import { updateTransfer } from '../../api/transfers/update-transfer'
import { ItemNotFound } from '../../components/elements/item-not-found'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import TransferForm from '../../components/pages/transfers/transfer-form'
import TransferPayments from '../../components/pages/transfers/transfer-payments'
import TransferReminders from '../../components/pages/transfers/transfer-reminders'
import { mapTransferToInput } from '../../components/pages/trip-bookings/utils'
import { Paths } from '../../lib/constants/path'
import { TransferInput } from '../../lib/types/models/transfer/transfer-input'
import { updateButtonsConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import NotFoundPage from '../common/not-found'

const TransferUpdatePage = () => {
  const { id } = useParams<{ id: string }>()
  const { navigate, back } = useNavigator()
  const queryClient = useQueryClient()

  const { mutateAsync } = useMutation({
    mutationFn: updateTransfer,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey: ['transfer', id] })
      toast.success('Transfert modifiée avec succès')
    }
  })

  const { data: transfer, isLoading } = useQuery({
    queryKey: ['transfer', id],
    queryFn: () => {
      if (!id || isNaN(+id)) return null
      return getTransfer(+id)
    }
  })

  const handleUpdate = async (values: TransferInput) => {
    if (!transfer) return
    await mutateAsync({ ...values, id: transfer.id })
  }

  if (!transfer && !isLoading)
    return <ItemNotFound title="Le transfert que vous cherchez n'existe pas" />
  if (isLoading) return <LoadingScreen />
  if (!transfer) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={`Transfert - ${transfer.id}`} icon={ArrowLeftRight} />
      <div className={'h-4'} />

      <Box>
        <TransferForm
          defaultValues={mapTransferToInput(transfer)}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.TRANSFER_LIST_PAGE),
            onUpdate: handleUpdate,
            onUpdateThenCreate: async (values) => {
              await handleUpdate(values)
              navigate(Paths.TRANSFER_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await handleUpdate(values)
              navigate(Paths.TRANSFER_LIST_PAGE, { replace: true })
            }
          })}
        />

        <div className={'mt-4 flex flex-col gap-4'}>
          <TransferReminders transferId={transfer.id} />
          <TransferPayments transferId={transfer.id} />
        </div>
      </Box>
    </div>
  )
}

export default TransferUpdatePage
